
import * as THREE from 'three';
import { Pane } from 'tweakpane';
import Background from "./background"
import VideoGrid from "./video-grid"

function trackDirection(value) {
  typeof value !== "object" && (value = { onUpdate: value });
  let prevTime = 0,
    prevReversed = false,
    anim = value.eventCallback ? value : value.animation,
    onUpdate = value.onUpdate,
    onToggle = value.onToggle;
  return anim
    ? anim.eventCallback(
        "onUpdate",
        trackDirection({ onUpdate: onUpdate, onToggle: onToggle })
      )
    : function () {
        let time = this.totalTime(),
          reversed = time < prevTime;
        this.direction = reversed ? -1 : 1;
        if (reversed !== prevReversed) {
          onToggle && onToggle.call(this, this.direction);
          prevReversed = reversed;
        }
        prevTime = time;
        onUpdate && onUpdate.call(this, this.direction);
      };
}

export default class Scene {
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera( 60, 1, 0.1, 60 );
    renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true
    });
    intersectable = false;
    pointer = new THREE.Vector2();
    components = {};
    PARAMS = {
        x: 0,
        y: 0,
        z: 0,
        a: 1
    }
    currentIndex = 0;
    isMuted = true;

    constructor({ el }) {
        this.$graphics = el
        this.videoTextures = []

        this.renderer.setClearAlpha( this.PARAMS.a );

    this.initialize();
        this.setComponents();

        this.handleResize();
        this.boundResize = this.handleResize.bind(this);
        ScrollTrigger.addEventListener("refresh", this.boundResize)
        window.addEventListener("resize", this.boundResize);

        this.renderer.setAnimationLoop(this.hanedleRender.bind(this));

        const controls = this.$graphics.querySelector(".toggle-play-pause")

        controls.addEventListener("click", () => {
            const htmlVideos = this.$graphics.parentElement.parentElement.querySelectorAll("video")
            controls.classList.toggle("paused")
            if (controls.classList.contains("paused")) {
                htmlVideos.forEach(video => video.pause())
            } else {
                htmlVideos.forEach(video => video.play())
            }
        })
        const soundControls = this.$graphics.querySelector(".toggle-sound")
        const htmlVideos = this.$graphics.parentElement.parentElement.querySelectorAll("video")
        if (soundControls) {
            soundControls.addEventListener("click", () => {
                soundControls.classList.toggle("muted")
                this.isMuted = soundControls.classList.contains("muted")
                
                // Sync the volume audio states immediately
                this.syncVideoAudio(htmlVideos)
            })
        }
    }
    syncVideoAudio(htmlVideos) {
        htmlVideos.forEach((video, idx) => {
            if (idx === this.currentIndex && !this.isMuted) {
                video.muted = false;
                video.volume = 1.0;
            } else {
                video.muted = true;
            }
        });
    }

    setComponents() {
        const { scene, renderer } = this;
        const htmlVideos = this.$graphics.parentElement.parentElement.querySelectorAll("video")
        this.syncVideoAudio(htmlVideos)
        this.components.videoGrid = new VideoGrid({ videos: htmlVideos })
        this.components.background = new Background({ scene, renderer })

        this.components.background.material.uniforms.tDiffuse.value = this.components.videoGrid.videoTextures[0]

        const animation = new gsap.timeline();
        animation.add(this.toggleSlide(1, htmlVideos), 'a')
        animation.add(this.toggleSlide(2, htmlVideos), 'b')
        
        this.st = ScrollTrigger.create({
            trigger: this.$graphics,
            start: 'top top',
            pinType: 'fixed',
            pin: true,
            end: 'bottom bottom',
            endTrigger:  this.$graphics.parentElement,
            pinSpacing: false,
            animation,
            scrub: true
        });


        
        
    }

    toggleSlide(index, htmlVideos) {
        // const lerper = (v0, v1, t) => v0 + (v1 - v0) * t;
        const smoothStepFunc = gsap.utils.pipe(
            gsap.utils.clamp(0.1, 0.9),                // Force input into the active window
            gsap.utils.mapRange(0.1, 0.9, 0, 1),      // Map that window to 0-1
    );
        // const lerper = gsap.utils.interpolate(0.25, 0.75, smoothstep(val));
        this.components.background.material.uniforms.tDiffuse2.value = this.components.videoGrid.videoTextures[index]
        const lerp = {
            x: this.components.background.material.uniforms.dispFactor.value
        }
        const timeline = new gsap.timeline()
        timeline.to(lerp, {
            x: 1,
            duration: 10,
            onStart: () => {
                this.components.background.material.uniforms.tDiffuse2.value = this.components.videoGrid.videoTextures[index]
                this.components.background.material.uniforms.tDiffuse.value = this.components.videoGrid.videoTextures[index - 1]
                
                this.currentIndex = index;
                this.syncVideoAudio(htmlVideos);
            },

            onUpdate: trackDirection({
                onToggle: (direction) => {
                    if (direction === -1) {
                        this.components.background.material.uniforms.tDiffuse2.value = this.components.videoGrid.videoTextures[index]
                        this.components.background.material.uniforms.tDiffuse.value = this.components.videoGrid.videoTextures[index - 1]

                        this.currentIndex = index - 1;
                        this.syncVideoAudio(htmlVideos);
                    }
                },
                onUpdate: (direction) => {
                    this.components.background.material.uniforms.dispFactor.value = smoothStepFunc(lerp.x);
                },
            }),
            onComplete: () => {
                this.components.background.material.uniforms.tDiffuse.value = this.components.videoGrid.videoTextures[index]
            }
        })
        
        return timeline
    }

    initialize() {
        const { $graphics } = this;
        $graphics?.appendChild( this.renderer.domElement );

        this.camera.position.z = 3.14;
    }


    hanedleRender() {        
        this.camera.updateMatrixWorld();
        Object.keys(this.components).forEach(_ => {
            if ("handleRender" in this.components[_]) this.components[_].handleRender(this.pane);
        });


        this.renderer.render(this.scene, this.camera);
    }

    handleResize() {
        const width = this.$graphics.offsetWidth;
        const height = this.$graphics.offsetHeight;
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize( width, height );

        Object.keys(this.components).forEach(_ => {
            if ("handleResize" in this.components[_]) this.components[_].handleResize(width, height);
        });
    }

    setDebug() {
        this.pane = new Pane();
        Object.keys(this.components).forEach(_ => {
            if ("setDebug" in this.components[_]) this.components[_].setDebug(this.pane);
        });
    }

    dispose() {
        this.st?.kill()
        Object.keys(this.components).forEach(_ => {
            if ("dispose" in this.components[_]) {
                this.components[_].dispose();
                delete this.components[_];
            }
        });
        // this.renderer.setAnimationLoop(null);
        // this.renderer.dispose();

        // ScrollTrigger.removeEventListener("refresh", this.boundResize)
        // window.removeEventListener("resize", this.boundResize);

    }
}
